/**
 * json-render Registry — DramaDetector component registry + renderer
 *
 * Defines the registry that maps catalog component types to React implementations.
 * S4 (#787): Real component implementations wired up.
 *
 * The catalog definition lives in dd-catalog.ts (separated to avoid pulling in
 * react-native at build time for scripts like generate-system-prompt.ts).
 *
 * Architecture ref: Winston (Discourse #3543/2) -- Section 2 + 4
 * Catalog spec ref: Sally (Discourse #3543/4)
 * Implementation: 10 web wrappers in ./components/ (web-only, Phase 1)
 *
 * NOTE: DramaScoreGauge is NOT in this catalog -- stays in non-generative
 * views per Sally's decision (animated SVG, non-generative).
 *
 * Task: [#783.S4] NocoDB #787
 * Agent: Diego <diego@liftping.net>
 */

import React from "react";
import { defineRegistry } from "@json-render/react-native";
import type { ZodType } from "zod";

import {
  AnalysisCardPropsSchema,
  DramaScorePropsSchema,
  DramaCategoryBadgePropsSchema,
  NarrativeBlockPropsSchema,
  TierBadgePropsSchema,
  InsightRowPropsSchema,
  SectionDividerPropsSchema,
  ActionButtonPropsSchema,
  PatternSpotlightPropsSchema,
  TransformationInsightPropsSchema,
  StyleConsistencyInsightPropsSchema,
  CommunicationPatternInsightPropsSchema,
  ModuleProgressPropsSchema,
} from "./schema";

// S4 + P3.4a: Component implementations
// PatternSpotlight, TransformationInsight, ActionButton → real RN component adapters
// AnalysisCard, DramaScore, DramaCategoryBadge, NarrativeBlock → web wrappers
// InsightRow, TierBadge, SectionDivider → enhanced web wrappers (real components in apps/mobile/)
import {
  AnalysisCard,
  DramaScore,
  DramaCategoryBadge,
  NarrativeBlock,
  TierBadge,
  InsightRow,
  SectionDivider,
  ActionButton,
  PatternSpotlight,
  TransformationInsight,
  StyleConsistencyInsight,
  CommunicationPatternInsight,
  ModuleProgress,
} from "./components";

// Catalog definition (no RN runtime dependency) — imported below for defineRegistry,
// re-exported here for consumers that import from this module.
import { ddCatalog } from "./dd-catalog";
export { ddCatalog };

// ============================================================================
// safeParse helper -- fail-closed with safe defaults per component
//
// Uses safeParse() for runtime safety without crashing on malformed LLM output.
// On validation failure: merges safe defaults (covering all required fields)
// with the raw props so valid fields are preserved while invalid/missing ones
// get safe fallback values. This prevents RN component crashes on bad props.
//
// Prerequisite for P3.4 (real RN component wrappers).
// Task: [#1158.SDUI-M.P3.5]
// ============================================================================

// Safe defaults for each component — used when LLM generates invalid props.
// Values align with Zod .default() calls in schema.ts for optional fields,
// and use safe render-friendly values for required fields.
export const COMPONENT_DEFAULTS: Record<string, Record<string, unknown>> = {
  AnalysisCard: { title: "Analysis Results", variant: "standard" },
  DramaScore: { score: 0, category: "safe", size: "md", showLabel: true },
  DramaCategoryBadge: { category: "safe", size: "md", showIcon: true },
  NarrativeBlock: { text: "", emphasis: "normal" },
  TierBadge: { tier: "free", showUpgradeHint: false },
  InsightRow: { title: "", description: "" },
  SectionDivider: { variant: "subtle", spacing: "md" },
  ActionButton: {
    label: "Action",
    action: "dismiss_insight",
    tier: "free",
    variant: "primary",
    disabled: false,
  },
  PatternSpotlight: {
    patternName: "",
    description: "",
    severity: "low",
    occurrences: 1,
  },
  TransformationInsight: {
    title: "",
    before: "",
    after: "",
    rationale: "",
    category: "safe",
    impactScore: 0,
  },
  StyleConsistencyInsight: {
    formalityShift: 0,
    vocabularyDiversity: 0,
    punctuationPattern: "mixed",
    messageLengthCategory: "moderate",
    flags: [],
  },
  CommunicationPatternInsight: {
    aiLikelihood: 0,
    label: "natural",
    hasBaseline: false,
    signals: [],
  },
  ModuleProgress: {
    moduleId: "",
    moduleName: "",
    current: 0,
    required: 1,
    confidence: "none",
    isPremium: true,
  },
};

// Track warned components to avoid log spam (one warning per component type per session)
const _warnedComponents = new Set<string>();

export function parseProps<T>(
  componentName: string,
  rawProps: Record<string, unknown>,
  zodSchema: ZodType<T>
): T {
  const result = zodSchema.safeParse(rawProps);
  if (result.success) {
    return result.data;
  }
  // Log once per component type to avoid render-loop log spam
  if (!_warnedComponents.has(componentName)) {
    _warnedComponents.add(componentName);
    console.warn(
      `[ddRegistry] ${componentName} props validation failed, using defaults:`,
      result.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`)
    );
  }
  // Fail-closed: start with safe defaults, then overlay ONLY the raw props
  // that individually pass their field-level schema. This prevents wrong-type
  // fields (e.g., score: "seven") from overriding safe defaults while keeping
  // valid fields (e.g., category: "high_drama") from the LLM output.
  const defaults = COMPONENT_DEFAULTS[componentName] ?? {};
  const merged = { ...defaults };
  const shape = (zodSchema as any)._def?.shape?.();
  if (shape) {
    for (const [key, value] of Object.entries(rawProps)) {
      const fieldSchema = shape[key];
      if (fieldSchema) {
        const fieldResult = fieldSchema.safeParse(value);
        if (fieldResult.success) {
          merged[key] = fieldResult.data;
        }
      }
    }
  } else {
    // Fallback if schema shape isn't accessible: use defaults only
    // (safer than passing unvalidated rawProps)
  }
  return merged as T;
}

// ============================================================================
// Registry -- S4: Real implementations wired up
//
// Prop-mapping strategy (P3.4a: 6 components wired to real RN components):
//   AnalysisCard          -> AnalysisCard (analysis-card.tsx) -- web wrapper, direct pass-through
//   DramaScore            -> DramaScore (drama-score-web.tsx) -- web wrapper, drama-score.tsx helpers
//   DramaCategoryBadge    -> DramaCategoryBadge (drama-category-badge.tsx) -- web wrapper, drama-badge.tsx
//   NarrativeBlock        -> NarrativeBlock (narrative-block.tsx) -- web wrapper, p+tailwind
//   TierBadge             -> TierBadge (tier-badge.tsx) -- web wrapper (no RN equivalent in packages/ui)
//   InsightRow            -> InsightRow (insight-row.tsx) -- enhanced web wrapper (glassmorphism card)
//   SectionDivider        -> SectionDivider (section-divider.tsx) -- web wrapper, hr+tailwind
//   ActionButton          -> TierButton adapter (action-button.tsx) -- REAL component [P3.4a]
//   PatternSpotlight      -> PatternSpotlightCard adapter (pattern-spotlight-web.tsx) -- REAL component [P3.4a]
//   TransformationInsight -> TransformationInsightCard adapter (transformation-insight-web.tsx) -- REAL component [P3.4a]
//
// DramaScoreGauge: NOT in catalog (animated SVG, non-generative per Sally)
//
// Runtime Zod validation: All rawProps are validated via safeParse() before
// passing to components. Invalid props degrade gracefully with a console warning.
// See: Audit #1158 -- type cast safety gap remediation.
//
// Action wiring: Actions are default no-ops here. DDGenerativeResults wires
// real implementations via JSONUIProvider handlers (upgrade_tier -> router.push,
// share_result -> useShareAnalysis, etc.). These defaults exist as a safety net
// when the catalog is used outside of DDGenerativeResults.
// ============================================================================

export const { registry: ddRegistry } = defineRegistry(ddCatalog, {
  components: {
    AnalysisCard: ({ props: rawProps, children }) => {
      const props = parseProps("AnalysisCard", rawProps, AnalysisCardPropsSchema);
      return <AnalysisCard {...props}>{children}</AnalysisCard>;
    },

    DramaScore: ({ props: rawProps }) => {
      const props = parseProps("DramaScore", rawProps, DramaScorePropsSchema);
      return <DramaScore {...props} />;
    },

    DramaCategoryBadge: ({ props: rawProps }) => {
      const props = parseProps("DramaCategoryBadge", rawProps, DramaCategoryBadgePropsSchema);
      return <DramaCategoryBadge {...props} />;
    },

    NarrativeBlock: ({ props: rawProps }) => {
      const props = parseProps("NarrativeBlock", rawProps, NarrativeBlockPropsSchema);
      return <NarrativeBlock {...props} />;
    },

    TierBadge: ({ props: rawProps }) => {
      const props = parseProps("TierBadge", rawProps, TierBadgePropsSchema);
      return <TierBadge {...props} />;
    },

    InsightRow: ({ props: rawProps }) => {
      const props = parseProps("InsightRow", rawProps, InsightRowPropsSchema);
      return <InsightRow {...props} />;
    },

    SectionDivider: ({ props: rawProps }) => {
      const props = parseProps("SectionDivider", rawProps, SectionDividerPropsSchema);
      return <SectionDivider {...props} />;
    },

    ActionButton: ({ props: rawProps, emit }) => {
      const props = parseProps("ActionButton", rawProps, ActionButtonPropsSchema);
      return (
        <ActionButton
          {...props}
          onPress={() => emit?.(props.action ?? "press")}
        />
      );
    },

    PatternSpotlight: ({ props: rawProps }) => {
      const props = parseProps("PatternSpotlight", rawProps, PatternSpotlightPropsSchema);
      return <PatternSpotlight {...props} />;
    },

    TransformationInsight: ({ props: rawProps }) => {
      const props = parseProps("TransformationInsight", rawProps, TransformationInsightPropsSchema);
      return <TransformationInsight {...props} />;
    },

    StyleConsistencyInsight: ({ props: rawProps }) => {
      const props = parseProps("StyleConsistencyInsight", rawProps, StyleConsistencyInsightPropsSchema);
      return <StyleConsistencyInsight {...props} />;
    },

    CommunicationPatternInsight: ({ props: rawProps }) => {
      const props = parseProps("CommunicationPatternInsight", rawProps, CommunicationPatternInsightPropsSchema);
      return <CommunicationPatternInsight {...props} />;
    },

    ModuleProgress: ({ props: rawProps }) => {
      const props = parseProps("ModuleProgress", rawProps, ModuleProgressPropsSchema);
      return <ModuleProgress {...props} />;
    },
  },

  actions: {
    upgrade_tier: async (_params) => {
      // Default no-op. Wired to router.push("/subscription") via
      // DDGenerativeResults JSONUIProvider handlers (S3 #786).
    },
    share_result: async (_params) => {
      // Default no-op. Wired to useShareAnalysis() via
      // DDGenerativeResults JSONUIProvider handlers (S3 #786).
    },
    save_analysis: async (_params) => {
      // No-op -- analysis persistence is handled by the existing history flow.
      // No additional wiring needed.
    },
    dismiss_insight: async (_params) => {
      // No-op -- dismiss is a UI-only action (hide element from view).
      // Stateful dismiss requires a dismissedInsights state in JSONUIProvider.
    },
  },
});

// Convenience re-export for DDGenerativeResults (S3)
export { Renderer as DDRenderer } from "@json-render/react-native";
export type { Catalog } from "@json-render/core";
